import type {
  OpeningsCustomerAppointmentItem,
  OpeningsService,
} from "@/lib/api/openingsTypes";

/**
 * Why Openings will not reschedule an appointment (booking standard
 * `BF-DEC-010`, § 8), shared by the website's reschedule and the mobile BFF. A reschedule only moves the time: it keeps the booked
 * services and options, cannot move a paid appointment, and can change the
 * stylist only for a plain booking. Anything else is a cancel and a new
 * booking, so each message says so and that the original is unchanged.
 */
export type RescheduleRefusal =
  | "paid"
  | "services_changed"
  | "provider_change"
  | "payment_pending"
  | "unavailable";

const UNCHANGED = "Your appointment hasn't changed.";

export const RESCHEDULE_REFUSAL_MESSAGES: Record<RescheduleRefusal, string> = {
  paid: `This appointment was paid in advance, so it can't be moved here. To change it, cancel this appointment and book a new time. Cancelling before the cancellation window refunds your payment automatically. ${UNCHANGED}`,
  services_changed: `Rescheduling keeps the same services. To change them, cancel this appointment and book a new one. ${UNCHANGED}`,
  provider_change: `This appointment can't be moved to another stylist. Keep the same stylist, or cancel this appointment and book a new one. ${UNCHANGED}`,
  payment_pending: `This appointment's payment is still being confirmed, so it can't be changed yet. Please try again later or contact the salon. ${UNCHANGED}`,
  unavailable: `This appointment can't be rescheduled in the app. Contact the salon, or cancel it and book a new time. ${UNCHANGED}`,
};

/**
 * Openings' older refusal for a changed service selection: a plain
 * `VALIDATION_ERROR` told apart only by this exact message. Kept so the BFF
 * keeps working until Openings answers `RESCHEDULE_SERVICES_CHANGED`
 * everywhere.
 */
const SERVICES_CHANGED_MESSAGE =
  "Services and options cannot be changed while rescheduling";

/**
 * Classifies an Openings reschedule refusal by HTTP status + structured code.
 * The changed-services refusal is `422 RESCHEDULE_SERVICES_CHANGED`, or for
 * an Openings deploy that predates it, `422 VALIDATION_ERROR` with the exact
 * message (the same narrow-allowlist rule `isSlotUnavailableError` follows).
 * Anything else returns `null` and keeps propagating as an outage.
 */
export const rescheduleRefusalFromError = (
  error: unknown,
): RescheduleRefusal | null => {
  if (!error || typeof error !== "object") return null;
  const candidate = error as { status?: unknown; body?: unknown };
  const body = candidate.body as
    | { error?: { code?: unknown; message?: unknown } }
    | undefined;
  const code = body?.error?.code;
  if (candidate.status === 409) {
    if (code === "PAID_APPOINTMENT_RESCHEDULE_UNSUPPORTED") return "paid";
    if (code === "PROVIDER_CHANGE_REQUIRES_NEW_BOOKING") return "provider_change";
    if (code === "PAYMENT_STATUS_UNKNOWN") return "payment_pending";
  }
  if (candidate.status === 422) {
    if (code === "RESCHEDULE_SERVICES_CHANGED") return "services_changed";
    if (code === "VALIDATION_ERROR" && body?.error?.message === SERVICES_CHANGED_MESSAGE) {
      return "services_changed";
    }
  }
  return null;
};

/**
 * A settled upfront payment on the appointment. Openings refuses to
 * reschedule it (`PAID_APPOINTMENT_RESCHEDULE_UNSUPPORTED`), so the BFF says
 * so before the reschedule call, and so before any verification code is sent.
 */
export const hasSettledUpfrontPayment = (
  item: Pick<OpeningsCustomerAppointmentItem, "payment">,
): boolean => {
  const paid = item.payment?.amountPaidMinor;
  return typeof paid === "number" && Number.isFinite(paid) && paid > 0;
};

type Selection = ReadonlyArray<{ id: string; optionId?: string }>;

type BookedServices = ReadonlyArray<{
  id?: string | null;
  option?: { id?: string | null } | null;
}>;

/**
 * The booked services as host catalog ids — what the location's booking
 * catalog publishes and availability accepts — so a reschedule can offer
 * times for exactly what was booked. A stylist's clone resolves through its
 * `referenceId`. `null` when any line is synthesized or not in the catalog.
 */
export const bookedHostSelection = (args: {
  booked: BookedServices;
  catalog: readonly OpeningsService[];
  appointmentId: string;
}): { id: string; optionId?: string }[] | null => {
  const byId = new Map(args.catalog.map((service) => [service.id, service]));
  const result: { id: string; optionId?: string }[] = [];
  for (const entry of args.booked) {
    if (!entry.id || entry.id === args.appointmentId) return null;
    const service = byId.get(entry.id);
    if (!service) return null;
    const id = service.referenceId || service.id;
    const optionId = entry.option?.id;
    if (!optionId) {
      result.push({ id });
      continue;
    }
    const option = service.options?.find((candidate) => candidate.id === optionId);
    if (!option) return null;
    result.push({ id, optionId: option.referenceId || option.id });
  }
  return result.length > 0 ? result : null;
};

/**
 * A selection as host catalog ids, order-independent. A stylist's service is
 * a clone whose `referenceId` is the host service (and likewise for options),
 * so two selections are "the same services" when they resolve to the same
 * host ids. `null` when any id is not in the catalog: unknown, never a guess.
 */
const hostSelectionKey = (
  selection: Selection,
  catalog: readonly OpeningsService[],
): string | null => {
  const byId = new Map(catalog.map((service) => [service.id, service]));
  const parts: string[] = [];
  for (const entry of selection) {
    const service = byId.get(entry.id);
    if (!service) return null;
    const hostId = service.referenceId || service.id;
    let hostOption = "";
    if (entry.optionId) {
      const option = service.options?.find((candidate) => candidate.id === entry.optionId);
      if (!option) return null;
      hostOption = option.referenceId || option.id;
    }
    parts.push(`${hostId}:${hostOption}`);
  }
  return parts.sort().join(",");
};

/**
 * Whether the app's chosen services differ from the booked ones.
 * `true`/`false` when both resolve against the schedule catalog; `null` when
 * either cannot be resolved (a synthesized review id, a retired service), in
 * which case Openings decides and its refusal is mapped afterwards.
 */
export const rescheduleSelectionChanged = (args: {
  requested: Selection;
  booked: ReadonlyArray<{ id?: string | null; option?: { id?: string | null } | null }>;
  catalog: readonly OpeningsService[];
  appointmentId: string;
}): boolean | null => {
  const booked: { id: string; optionId?: string }[] = [];
  for (const entry of args.booked) {
    if (!entry.id || entry.id === args.appointmentId) return null;
    booked.push(entry.option?.id ? { id: entry.id, optionId: entry.option.id } : { id: entry.id });
  }
  if (booked.length === 0) return null;
  const requestedKey = hostSelectionKey(args.requested, args.catalog);
  const bookedKey = hostSelectionKey(booked, args.catalog);
  if (requestedKey === null || bookedKey === null) return null;
  return requestedKey !== bookedKey;
};
